import {
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  makeStyles,
  type CheckboxOnChangeData,
  type SelectionEvents,
} from "@fluentui/react-components";
import * as React from "react";
import { CheckboxAccordionItem } from "./CheckboxAccordionItem";
import type { Filter } from "./types";
import { usePromptFilterLocalizations } from "./usePromptFilterStrings";
import { groupFilters } from "./utils";

const useStyles = makeStyles({
  filterHeader: {
    "& button": {
      fontWeight: 600,
    },
  },

  filterList: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
});

type Props = {
  /**
   * Filter for this dropdown.
   */
  filter: Filter;

  /**
   * Selected values for this dropdown. It is used to fully control the dropdown.
   */
  selectedValues?: string[];

  /**
   * Callback for changed event.
   */
  onChange: (ev: SelectionEvents, newValue: string[]) => void;
};

export const PromptFilterAccordionItem: React.FC<Props> = ({
  filter,
  selectedValues,
  onChange,
}) => {
  const styles = useStyles();
  const { facetKeyToLabel } = usePromptFilterLocalizations();
  const filterByGroup = groupFilters(filter);

  const handleOnChange = (
    ev: React.ChangeEvent<HTMLInputElement>,
    data: CheckboxOnChangeData
  ) => {
    if (data.checked) {
      if (filter.isSingleSelect) {
        onChange(ev, [ev.target.value]);
      } else {
        onChange(ev, [...(selectedValues ?? []), ev.target.value]);
      }
    } else {
      onChange(
        ev,
        (selectedValues ?? []).filter((v) => v !== ev.target.value)
      );
    }
  };

  if (filter.options.length === 0) {
    return null;
  }

  return (
    <AccordionItem value={filter.key} key={filter.key}>
      <AccordionHeader
        expandIconPosition="end"
        className={styles.filterHeader}
        size="large"
      >
        {facetKeyToLabel(filter.key)}
      </AccordionHeader>
      <AccordionPanel className={styles.filterList}>
        {Object.entries(filterByGroup).map(
          ([groupKey, filterOptions], index, array) => (
            <CheckboxAccordionItem
              key={filter.key}
              groupKey={groupKey}
              filterOptions={filterOptions}
              selectedValues={selectedValues}
              onChange={handleOnChange}
              isLastItem={index === array.length - 1}
            />
          )
        )}
      </AccordionPanel>
    </AccordionItem>
  );
};
